import React, { useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  useRecordContext,
  useRedirect,
  useTranslate,
  usePermissions,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '../../utils/hasPermission';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  Bolt,
  AccessTime,
  Payment,
  LocalParking,
  Speed,
  Thermostat,
  Battery20,
  BatteryFull,
  AccountBalanceWallet,
  CreditCard,
  Badge,
  QrCode2,
  Receipt,
  Warning,
  CheckCircle,
  Cancel,
  Schedule,
  Person,
  AttachMoney,
  History,
  EvStation,
  Summarize,
  ShowChart,
  Settings,
  Router,
  Power,
  FlashOn,
  BatteryCharging80,
  Timer,
  StopCircle,
  PlayArrow,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { MeterValuesCharts } from './MeterValuesCharts';
import { OcppLogs } from './OcppLogs';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`session-tabpanel-${index}`}
      aria-labelledby={`session-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Section Header Component
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  color?: string;
}

const SectionHeader = ({ icon, title, subtitle, color = 'primary.main' }: SectionHeaderProps) => {
  const iconElement = React.cloneElement(icon as React.ReactElement<any>, { sx: { color } });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        backgroundColor: `${color.replace('.main', '.light')}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 2
      }}>
        {iconElement}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );
};

// Payment method icon
const PaymentMethodIcon = ({ method }: { method: string }) => {
  switch (method) {
    case 'wallet': return <AccountBalanceWallet />;
    case 'credit_card': return <CreditCard />;
    case 'rfid': return <Badge />;
    case 'qr_code': return <QrCode2 />;
    default: return <Payment />;
  }
};

// Payment status color — used for both session-level payment_status
// (completed/failed/refunded/...) AND per-transaction status (hold/released/...)
// surfaced from the linked app_transactions row.
const getPaymentStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'success';
    case 'released': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'error';
    case 'refunded': return 'warning';
    case 'partial_refund': return 'info';
    case 'hold': return 'info';
    case 'pending': return 'default';
    default: return 'default';
  }
};

// Format duration
const formatDuration = (seconds: number) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

// Format duration with minute:second for idle time (more precise)
const formatIdleTime = (seconds: number) => {
  if (!seconds) return '0s';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (minutes > 0) {
    return `${minutes}:${secs.toString().padStart(2, '0')} (${seconds}s)`;
  }
  return `${seconds}s`;
};

// Build the per-line text for the "this session" time-fee caption rendered under the
// Time Fee row in CostInfo. Extracted as a pure helper so the windowed / single-window
// / unit-label / context-suffix branches can be tested without mounting the whole Show.
// `translate` is the react-admin translate function; only minUnit/hrUnit/max/free keys
// are consumed here.
export const buildTimeFeeCaptionLines = (
  tf: any,
  currency: string,
  translate: (key: string) => string,
): string[] => {
  const isHour = tf.unit === 'hour';
  const unitLabel = isHour
    ? translate('resources.charging-sessions.show.hrUnit')
    : translate('resources.charging-sessions.show.minUnit');
  const minUnit = translate('resources.charging-sessions.show.minUnit');
  const windows = Array.isArray(tf.windows) ? tf.windows : [];
  const ctx: string[] = [];
  if (tf.max_charge_minutes > 0) ctx.push(`${translate('resources.charging-sessions.show.max')}: ${tf.max_charge_minutes} ${minUnit}`);
  if (tf.free_minutes > 0) ctx.push(`${translate('resources.charging-sessions.show.free')}: ${tf.free_minutes} ${minUnit}`);
  const ctxStr = ctx.length > 0 ? ` (${ctx.join(' · ')})` : '';

  if (windows.length > 1) {
    const lines = windows.map(
      (w: any) => `${w.window}: ${w.units} ${unitLabel} × ${currency} ${w.rate}/${unitLabel} = ${currency} ${parseFloat(String(w.cost)).toFixed(2)}`,
    );
    if (ctxStr) lines.push(ctxStr.trim());
    return lines;
  }

  const rate = windows.length === 1 ? windows[0].rate : (tf.cost / (tf.chargeable_units || 1));
  return [`${tf.chargeable_units} ${unitLabel} × ${currency} ${rate}/${unitLabel}${ctxStr}`];
};

// Page Header Component
const PageHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const navigate = useNavigate();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { permissions } = usePermissions();
  const canViewOcpp = hasPermission(
    permissions as string[] | null | undefined,
    'charging-sessions:view-ocpp:all',
  );

  if (!record) return null;

  const handleViewOcppLogs = () => {
    const params = new URLSearchParams({
      charger: record.charger_name || record.charger_id || '',
      range: 'custom',
      start: record.session_start_time || '',
      end: record.cable_disconnected_at || record.session_end_time || new Date().toISOString(),
    });
    navigate(`/ocpp-logs?${params.toString()}`);
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 2,
      mb: { xs: 2, sm: 4 }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => redirect('/charging-sessions')}
          size="large"
          sx={{
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {translate('resources.charging-sessions.show.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<CheckCircle />}
              label={translate(`resources.charging-sessions.paymentStatuses.${record.payment_status}`, { _: record.payment_status?.replace('_', ' ').toUpperCase() || 'UNKNOWN' })}
              color={getPaymentStatusColor(record.payment_status) as any}
              size="small"
            />
            <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
              {translate('resources.charging-sessions.show.sessionNumber')} #{record.app_session_id}
            </Typography>
            {record.invoice_number && (
              <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                • {translate('resources.charging-sessions.show.invoice')}: {record.invoice_number}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {canViewOcpp && (isMobile ? (
        <IconButton
          onClick={handleViewOcppLogs}
          sx={{
            flexShrink: 0,
            borderColor: 'primary.main',
            color: 'primary.main',
            border: '1px solid',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.light',
            },
          }}
        >
          <Router />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          startIcon={<Router />}
          onClick={handleViewOcppLogs}
          sx={{
            flexShrink: 0,
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.light',
            },
          }}
        >
          {translate('resources.charging-sessions.show.viewOcppLogs')}
        </Button>
      ))}
    </Box>
  );
};

// Session Overview - Card Style
const SessionOverview = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  const energyDelivered = parseFloat(record.energy_delivered_kwh || 0);
  const estimatedKwh = record.estimated_kwh ? parseFloat(record.estimated_kwh) : null;
  const percentage = estimatedKwh && estimatedKwh > 0
    ? Math.min((energyDelivered / estimatedKwh) * 100, 100)
    : null;

  const lastMeter = record.meter_values_compressed?.last;

  // SoC: if soc_start_percent is 0, charger likely reported 0 in Transaction.Begin before BMS communicated.
  // Fall back to first non-zero SoC from compressed samples.
  let socStart = record.soc_start_percent;
  if ((socStart === 0 || socStart == null) && record.meter_values_compressed?.samples?.length > 0) {
    const firstNonZero = record.meter_values_compressed.samples.find((s: any) => s.soc != null && s.soc > 0);
    if (firstNonZero) socStart = firstNonZero.soc;
  }
  const socEnd = record.soc_end_percent;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: 2,
            backgroundColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Bolt sx={{ color: 'success.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.charging-sessions.show.sessionSummary')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {translate('resources.charging-sessions.show.completedSessionFrom')} {format(new Date(record.session_start_time), 'PP')}
              </Typography>
              {record.initiated_by && (
                <Chip label={record.initiated_by} size="small" variant="outlined" icon={<PlayArrow />} />
              )}
              {record.stop_reason && (
                <Chip label={`Stop: ${record.stop_reason}`} size="small" variant="outlined" icon={<StopCircle />} />
              )}
              {record.charging_phase_type && (
                <Chip label={record.charging_phase_type === 'three' ? '3-Phase' : '1-Phase'} size="small" variant="outlined" icon={<FlashOn />} />
              )}
            </Box>
          </Box>
          <Typography variant="h5" color="primary" fontWeight="600" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            {record.currency} {parseFloat(record.final_cost || 0).toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {energyDelivered.toFixed(2)} kWh {translate('resources.charging-sessions.show.energyDelivered').toLowerCase()}
            </Typography>
            {estimatedKwh && estimatedKwh > 0 && (
              <Typography variant="body2" color="text.secondary">
                Target: {estimatedKwh.toFixed(2)} kWh
              </Typography>
            )}
          </Box>
          {estimatedKwh && estimatedKwh > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={percentage || 0}
                  sx={{
                    height: 16,
                    borderRadius: 2,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: (percentage || 0) > 80 ? 'success.main' : 'primary.main'
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 40 }}>
                {(percentage || 0).toFixed(0)}%
              </Typography>
            </Box>
          )}
          {socStart != null && socEnd != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BatteryCharging80 sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                SoC: {socStart}% → {socEnd}%
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Bolt sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{translate('resources.charging-sessions.show.energyDelivered')}</Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {energyDelivered.toFixed(3)} kWh
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Timer sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{translate('resources.charging-sessions.show.sessionDuration')}</Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {(() => {
                if (record.charging_start_time && record.cable_disconnected_at) {
                  const start = new Date(record.charging_start_time).getTime();
                  const end = new Date(record.cable_disconnected_at).getTime();
                  const totalSeconds = Math.floor((end - start) / 1000);
                  return formatDuration(totalSeconds);
                }
                const total = (record.charging_duration_seconds || 0) + (record.idle_seconds || record.idle_duration_seconds || 0);
                return formatDuration(total);
              })()}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Speed sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Max Power</Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {parseFloat(record.max_power_kw || 0).toFixed(2)} kW
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Speed sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{translate('resources.charging-sessions.show.averagePower')}</Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {parseFloat(record.avg_power_kw || 0).toFixed(2)} kW
            </Typography>
          </Box>
          {lastMeter?.voltage_v != null && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Power sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">Peak Voltage</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                {lastMeter.voltage_v.toFixed(1)} V
              </Typography>
            </Box>
          )}
          {lastMeter?.current_a != null && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <FlashOn sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">Peak Current</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                {lastMeter.current_a.toFixed(1)} A
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const SessionInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<EvStation />}
          title={translate('resources.charging-sessions.show.sessionInformation')}
          color="primary.main"
        />
        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.sessionId')}</strong></TableCell>
              <TableCell><code>{record.app_session_id}</code></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.transactionId')}</strong></TableCell>
              <TableCell><code>{record.transaction_id}</code></TableCell>
            </TableRow>
            {record.invoice_number && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.invoiceNumber')}</strong></TableCell>
                <TableCell>{record.invoice_number}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.sessionDuration')}</strong></TableCell>
              <TableCell>
                {(() => {
                  // Calculate actual total duration from charging started to cable disconnect
                  if (record.charging_start_time && record.cable_disconnected_at) {
                    const start = new Date(record.charging_start_time).getTime();
                    const end = new Date(record.cable_disconnected_at).getTime();
                    const totalSeconds = Math.floor((end - start) / 1000);
                    return formatDuration(totalSeconds);
                  }
                  // Fallback: charging + idle
                  const total = (record.charging_duration_seconds || 0) + (record.idle_seconds || record.idle_duration_seconds || 0);
                  return formatDuration(total);
                })()}
                <Typography variant="caption" display="block" color="text.secondary">
                  {translate('resources.charging-sessions.show.fromChargingStartedToCableDisconnect')}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.chargingDuration')}</strong></TableCell>
              <TableCell>{formatDuration(record.charging_duration_seconds)}</TableCell>
            </TableRow>
            {record.charging_start_time && record.charging_completed_at && (
              <>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.chargingStarted')}</strong></TableCell>
                  <TableCell>{format(new Date(record.charging_start_time), 'PPpp')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.chargingCompleted')}</strong></TableCell>
                  <TableCell>{format(new Date(record.charging_completed_at), 'PPpp')}</TableCell>
                </TableRow>
              </>
            )}
            {record.cable_disconnected_at && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.cableDisconnected')}</strong></TableCell>
                <TableCell>{format(new Date(record.cable_disconnected_at), 'PPpp')}</TableCell>
              </TableRow>
            )}
            {(record.idle_duration_seconds > 0 || record.idle_seconds > 0) && (
              <>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.idleDuration')}</strong></TableCell>
                  <TableCell>
                    <Chip
                      label={formatIdleTime(record.idle_seconds || record.idle_duration_seconds)}
                      size="small"
                      color="warning"
                    />
                  </TableCell>
                </TableRow>
                {record.fee_structure_snapshot?.components?.idleFee && (
                  <>
                    <TableRow>
                      <TableCell><strong>{translate('resources.charging-sessions.show.gracePeriod')}</strong></TableCell>
                      <TableCell>
                        {(() => {
                          const gracePeriodMin = record.fee_structure_snapshot.components.idleFee.graceMinutes || 0;
                          const gracePeriodSec = gracePeriodMin * 60;
                          return formatIdleTime(gracePeriodSec);
                        })()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>{translate('resources.charging-sessions.show.chargeableIdleTime')}</strong></TableCell>
                      <TableCell>
                        {(() => {
                          const gracePeriodMin = record.fee_structure_snapshot.components.idleFee.graceMinutes || 0;
                          const gracePeriodSec = gracePeriodMin * 60;
                          const totalIdleSec = record.idle_seconds || record.idle_duration_seconds;
                          const chargeableSec = Math.max(0, totalIdleSec - gracePeriodSec);

                          return (
                            <Typography
                              component="span"
                              sx={{
                                color: chargeableSec > 0 ? 'error.main' : 'text.primary',
                                fontWeight: chargeableSec > 0 ? 'bold' : 'normal'
                              }}
                            >
                              {formatIdleTime(chargeableSec)}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const ChargerInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<LocalParking />}
          title={translate('resources.charging-sessions.show.chargerLocation')}
          color="info.main"
        />
        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.siteName')}</strong></TableCell>
              <TableCell>{record.site_name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.chargerName')}</strong></TableCell>
              <TableCell>{record.charger_name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.connectorId')}</strong></TableCell>
              <TableCell>#{record.connector_id}</TableCell>
            </TableRow>
            {record.site_address && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.address')}</strong></TableCell>
                <TableCell>{record.site_address}</TableCell>
              </TableRow>
            )}
            {record.max_power_kw && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.maxPower')}</strong></TableCell>
                <TableCell>{parseFloat(record.max_power_kw).toFixed(2)} kW</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const UserInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<Person />}
          title={translate('resources.charging-sessions.show.userInformation')}
          color="secondary.main"
        />
        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            {record.user?.numeric_user_id != null && (
              <TableRow>
                <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.userId', { _: 'User ID' })}</strong></TableCell>
                <TableCell>#{record.user.numeric_user_id}</TableCell>
              </TableRow>
            )}
            {(record.user?.name || record.user_name) && (
              <TableRow>
                <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.name')}</strong></TableCell>
                <TableCell>{record.user?.name || record.user_name}</TableCell>
              </TableRow>
            )}
            {(record.user?.email || record.user_email) && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.email')}</strong></TableCell>
                <TableCell>{record.user?.email || record.user_email}</TableCell>
              </TableRow>
            )}
            {record.user?.phone && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.phone', { _: 'Phone' })}</strong></TableCell>
                <TableCell>{record.user.phone}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={{ verticalAlign: 'top' }}><strong>{translate('resources.charging-sessions.show.vehicles', { _: 'Vehicles' })}</strong></TableCell>
              <TableCell>
                {Array.isArray(record.user?.vehicles) && record.user.vehicles.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {record.user.vehicles.map((v: any) => (
                      <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ fontSize: 'inherit' }}>
                          {[v.make, v.model].filter(Boolean).join(' ')}
                        </Typography>
                        {v.license_plate && (
                          <Chip
                            size="small"
                            label={v.license_plate}
                            sx={{ fontFamily: 'monospace' }}
                          />
                        )}
                        {v.is_primary && (
                          <Chip size="small" color="primary" variant="outlined" label={translate('resources.charging-sessions.show.primary', { _: 'Primary' })} />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  // Legacy fallback: pre-catalog rows had a single car_plate
                  // column on the session itself; show that if no vehicle list
                  // came back from the user lookup.
                  (record.car_plate || record.vehicle_model) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {record.vehicle_model && (
                        <Typography component="span" sx={{ fontSize: 'inherit' }}>{record.vehicle_model}</Typography>
                      )}
                      {record.car_plate && (
                        <Chip size="small" label={record.car_plate} sx={{ fontFamily: 'monospace' }} />
                      )}
                    </Box>
                  ) : (
                    <Typography component="span" color="text.secondary" sx={{ fontSize: 'inherit' }}>—</Typography>
                  )
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.initiatedBy')}</strong></TableCell>
              <TableCell>
                <Chip
                  label={translate(`resources.charging-sessions.types.${record.initiated_by}`, { _: record.initiated_by?.toUpperCase() })}
                  size="small"
                  color={record.initiated_by === 'app' ? 'primary' : 'secondary'}
                />
              </TableCell>
            </TableRow>
            {record.stop_reason && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.stopReason')}</strong></TableCell>
                <TableCell>{record.stop_reason}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export const CostInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<AttachMoney />}
          title={translate('resources.charging-sessions.show.costInformation')}
          color="warning.main"
        />
        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            {record.cost_breakdown_detail?.energy_costs?.length > 1 ? (
              <>
                {record.cost_breakdown_detail.energy_costs.map((window: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}>
                      <strong>{translate('resources.charging-sessions.show.energyCost')}</strong>
                      <Chip
                        label={window.window}
                        size="small"
                        color={window.window === 'peak' ? 'warning' : 'info'}
                        sx={{ ml: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      {window.energy_kwh} kWh × {record.currency} {window.rate}/kWh = {record.currency} {parseFloat(window.cost).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell><strong>Total Energy Cost</strong></TableCell>
                  <TableCell>
                    <strong>{record.currency} {parseFloat(record.energy_cost || 0).toFixed(2)}</strong>
                  </TableCell>
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.energyCost')}</strong></TableCell>
                <TableCell>
                  {record.currency} {parseFloat(record.energy_cost || 0).toFixed(2)}
                </TableCell>
              </TableRow>
            )}
            {record.session_fee > 0 && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.sessionFee')}</strong></TableCell>
                <TableCell>
                  {record.currency} {parseFloat(record.session_fee).toFixed(2)}
                </TableCell>
              </TableRow>
            )}
            {record.time_fee > 0 && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.timeFee')}</strong></TableCell>
                <TableCell>
                  {record.currency} {parseFloat(record.time_fee).toFixed(2)}
                  {record.cost_breakdown_detail?.time_fee && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {(() => {
                        const lines = buildTimeFeeCaptionLines(
                          record.cost_breakdown_detail.time_fee,
                          record.currency,
                          translate,
                        );
                        if (lines.length === 1) return lines[0];
                        return lines.map((line, i) => (
                          <span key={i} style={{ display: 'block' }}>{line}</span>
                        ));
                      })()}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {record.idle_cost > 0 && (
              <>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.idleFee')}</strong></TableCell>
                  <TableCell sx={{ color: 'warning.main' }}>
                    {record.currency} {parseFloat(record.idle_cost).toFixed(2)}
                    {record.cost_breakdown_detail?.idle_cost && record.fee_structure_snapshot?.components?.idleFee && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {(() => {
                          const idleCostDetail = record.cost_breakdown_detail.idle_cost;
                          const gracePeriodMin = record.fee_structure_snapshot.components.idleFee.graceMinutes || 0;
                          const gracePeriodSec = gracePeriodMin * 60;
                          const totalIdleSec = record.idle_seconds || record.idle_duration_seconds;
                          const chargeableSec = Math.max(0, totalIdleSec - gracePeriodSec);
                          const rate = idleCostDetail.rate || record.fee_structure_snapshot.components.idleFee.rate || 0;

                          return (
                            <>
                              {translate('resources.charging-sessions.show.idle')}: {formatIdleTime(totalIdleSec)}
                              {gracePeriodSec > 0 && ` (${translate('resources.charging-sessions.show.grace')}: ${formatIdleTime(gracePeriodSec)})`}
                              {' → '}
                              {translate('resources.charging-sessions.show.chargeable')}: {formatIdleTime(chargeableSec)} × {record.currency} {rate}/min
                            </>
                          );
                        })()}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </>
            )}
            {record.discount_amount && (
              <TableRow>
                <TableCell>
                  <strong>{translate('resources.charging-sessions.show.discount')}</strong>
                  {record.discount_code && ` (${record.discount_code})`}
                </TableCell>
                <TableCell sx={{ color: 'success.main' }}>
                  -{record.currency} {parseFloat(record.discount_amount).toFixed(2)}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.finalCost')}</strong></TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight="bold" color="primary.main">
                  {record.currency} {parseFloat(record.final_cost || 0).toFixed(2)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.meterReading')}</strong></TableCell>
              <TableCell>
                {translate('resources.charging-sessions.show.energyDelivered')}: {parseFloat(record.energy_delivered_kwh || 0).toFixed(3)} kWh
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const PaymentInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<Payment />}
          title={translate('resources.charging-sessions.show.paymentDetails')}
          color="success.main"
        />
        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.paymentMethod')}</strong></TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentMethodIcon method={record.payment_method} />
                  <Typography>{record.payment_method?.replace('_', ' ').toUpperCase()}</Typography>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.paymentStatus')}</strong></TableCell>
              <TableCell>
                <Chip
                  label={translate(`resources.charging-sessions.paymentStatuses.${record.payment_status}`, { _: record.payment_status?.replace('_', ' ').toUpperCase() })}
                  color={getPaymentStatusColor(record.payment_status) as any}
                  size="small"
                />
              </TableCell>
            </TableRow>
            {record.pre_auth_amount && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.preAuthorization')}</strong></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="span">
                      {record.currency} {parseFloat(record.pre_auth_amount).toFixed(2)}
                    </Typography>
                    {record.pre_auth_status && (
                      <Chip
                        label={translate(
                          `resources.charging-sessions.transactionStatuses.${record.pre_auth_status}`,
                          { _: record.pre_auth_status.replace('_', ' ').toUpperCase() },
                        )}
                        size="small"
                        color={getPaymentStatusColor(record.pre_auth_status) as any}
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {record.settlement_amount != null && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.actualCharged', { _: 'Actual Charged' })}</strong></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="span" fontWeight="bold">
                      {record.currency} {parseFloat(record.settlement_amount).toFixed(2)}
                    </Typography>
                    {record.settlement_status && (
                      <Chip
                        label={translate(
                          `resources.charging-sessions.transactionStatuses.${record.settlement_status}`,
                          { _: record.settlement_status.replace('_', ' ').toUpperCase() },
                        )}
                        size="small"
                        color={getPaymentStatusColor(record.settlement_status) as any}
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {record.settlement_completed_at && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.settlementDate')}</strong></TableCell>
                <TableCell>{format(new Date(record.settlement_completed_at), 'PPpp')}</TableCell>
              </TableRow>
            )}
            {record.settlement_transaction_id && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.settlementTransaction')}</strong></TableCell>
                <TableCell><code>{record.settlement_transaction_id}</code></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const TechnicalDetails = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  const hasData = record.temperature_avg_celsius || record.error_count > 0;
  if (!hasData) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<Speed />}
          title={translate('resources.charging-sessions.show.technicalDetails')}
          color="grey.700"
        />
        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            {record.temperature_avg_celsius && (
              <TableRow>
                <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.averageTemperature')}</strong></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Thermostat />
                    {record.temperature_avg_celsius}°C
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {record.error_count > 0 && (
              <TableRow>
                <TableCell><strong>{translate('resources.charging-sessions.show.errors')}</strong></TableCell>
                <TableCell>
                  <Chip
                    icon={<Warning />}
                    label={`${record.error_count} ${translate('resources.charging-sessions.show.errorCount')}`}
                    color="error"
                    size="small"
                  />
                  {record.last_error_code && ` - ${translate('resources.charging-sessions.show.last')}: ${record.last_error_code}`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const ArchiveInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<History />}
          title={translate('resources.charging-sessions.show.archiveInformation')}
          color="grey.600"
        />

      <Alert severity="info" sx={{ mb: 2 }}>
          {translate('resources.charging-sessions.show.archiveMessage')} {record.data_retention_days || translate('resources.charging-sessions.show.archiveMessageUnlimited')} {record.data_retention_days && translate('resources.charging-sessions.show.archiveMessageDays')}.
        </Alert>

        <Table size="small" sx={{ '& code': { wordBreak: 'break-all', fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.charging-sessions.show.archivedAt')}</strong></TableCell>
              <TableCell>{format(new Date(record.archived_at), 'PPpp')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.originalCreated')}</strong></TableCell>
              <TableCell>{format(new Date(record.created_at), 'PPpp')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.charging-sessions.show.dataRetention')}</strong></TableCell>
              <TableCell>{record.data_retention_days} {translate('resources.charging-sessions.show.days')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const SessionDetails = () => {
  const [tabValue, setTabValue] = useState(0);
  const record = useRecordContext();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { permissions } = usePermissions();
  const perms = permissions as string[] | null | undefined;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const hasTechnicalData = record && (record.temperature_avg_celsius || record.error_count > 0);

  // Build the tab list dynamically based on permissions. Overview is always
  // visible to anyone who reached this page (already gated by
  // charging-sessions:read:all on the show route). The other four tabs each
  // require a per-tab permission so admins can hand out granular access.
  const tabs = [
    {
      key: 'overview',
      label: translate('resources.charging-sessions.show.overview'),
      icon: <Summarize />,
      visible: true,
      content: (
        <>
          <SessionOverview />
          <SessionInfo />
          <ChargerInfo />
          <UserInfo />
        </>
      ),
    },
    {
      key: 'analytics',
      label: translate('resources.charging-sessions.show.analytics'),
      icon: <ShowChart />,
      visible: hasPermission(perms, 'charging-sessions:view-analytics:all'),
      content: <MeterValuesCharts />,
    },
    {
      key: 'payment',
      label: translate('resources.charging-sessions.show.payment'),
      icon: <Payment />,
      visible: hasPermission(perms, 'charging-sessions:view-payment:all'),
      content: (
        <>
          <CostInfo />
          <PaymentInfo />
        </>
      ),
    },
    {
      key: 'ocpp',
      label: translate('resources.charging-sessions.show.ocppLogs'),
      icon: <Router />,
      visible: hasPermission(perms, 'charging-sessions:view-ocpp:all'),
      content: <OcppLogs />,
    },
    {
      key: 'technical',
      label: translate('resources.charging-sessions.show.technical'),
      icon: <Settings />,
      visible: (hasTechnicalData || record?.archived_at) &&
        hasPermission(perms, 'charging-sessions:view-technical:all'),
      content: (
        <>
          {hasTechnicalData && <TechnicalDetails />}
          {record?.archived_at && <ArchiveInfo />}
        </>
      ),
    },
  ];
  const visibleTabs = tabs.filter(t => t.visible);

  // Clamp tabValue if a permission was revoked or some tab disappeared,
  // so we don't end up displaying an empty TabPanel.
  const safeTabValue = Math.min(tabValue, Math.max(visibleTabs.length - 1, 0));

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader />

      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={safeTabValue}
            onChange={handleTabChange}
            aria-label="session details tabs"
            variant={isMobile ? "fullWidth" : "scrollable"}
            scrollButtons={isMobile ? false : "auto"}
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 40, sm: 48 },
              '& .MuiTab-root': {
                minWidth: { xs: 0, sm: 90 },
                minHeight: { xs: 40, sm: 48 },
                px: { xs: 0.5, sm: 2 },
                py: { xs: 0.75, sm: 1.5 },
                fontSize: { xs: '0.65rem', sm: '0.875rem' },
                lineHeight: 1.1,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textTransform: 'none',
              },
            }}
          >
            {visibleTabs.map(tab => (
              <Tab
                key={tab.key}
                label={tab.label}
                icon={isMobile ? undefined : tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {visibleTabs.map((tab, idx) => (
          <TabPanel key={tab.key} value={safeTabValue} index={idx}>
            {tab.content}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export const ChargingSessionsShow = () => (
  <Show
    title={false}
    actions={false}
    component="div"
    sx={{
      maxWidth: '100%',
      overflow: 'hidden',
      backgroundColor: 'transparent',
      '& .RaShow-main': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    }}
  >
    <SimpleShowLayout
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
          px: { xs: 0, sm: 2 },
          py: { xs: 1, sm: 2 },
        },
        padding: { xs: 0, sm: 'inherit' },
      }}
    >
      <SessionDetails />
    </SimpleShowLayout>
  </Show>
);

export default ChargingSessionsShow;